import { useRef, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { FileArchive, TriangleAlert } from 'lucide-react';

import { Button, Dialog } from '../../../components/ui';
import { useT } from '../../../i18n';
import { dungDinhDangGoi, goiQuaLon, GOI_TOI_DA_BYTE } from '../nghiep-vu/thao-tac';
import type { ModulePackageResult } from '../types';
import { KetQuaNopGoi } from './KetQuaNopGoi';
import { LoiNopGoi } from './LoiNopGoi';
import { LoiTrongHop } from './LoiTrongHop';

function dungLuongVN(byte: number): string {
  const mb = byte / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

/**
 * Hộp nộp gói module (ADR-0045 mục 1: bố cục zip, kiểm bằng `module-registry`).
 *
 * Chỉ gửi tệp lên; backend đọc `module.yaml` trong gói để lấy mọi metadata
 * (`code`, `name`, `version`…) — form **không** có ô nhập lại những trường đó,
 * tránh hai nguồn sự thật lệch nhau giữa manifest và form. Kiểm chữ ký
 * Ed25519, quét Trivy và chạy thử migration chạy thật ở `module-registry`;
 * kết quả lỗi trả qua `error_code` của `422`/`409` (ADR-0045 mục 2), không có
 * endpoint riêng để xem lại sau — chỉ hiện ngay tại đây.
 *
 * Hai kênh dùng chung hộp này (ADR-0096 mục 8): `vendor` ở cổng nền tảng (gói phải ký), `tenant` ở màn Module của
 * cổng tenant (không ký, chờ quản trị tổ chức duyệt, ADR-0115). Nơi gọi truyền mutation nộp của đúng kênh.
 */
export function HopUploadModule({
  open,
  onClose,
  nop,
  kenh = 'vendor',
}: {
  open: boolean;
  onClose: () => void;
  nop: UseMutationResult<ModulePackageResult, unknown, File>;
  kenh?: 'vendor' | 'tenant';
}) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tep, setTep] = useState<File | null>(null);
  const [loiChonTep, setLoiChonTep] = useState<string | null>(null);

  const dong = () => {
    onClose();
    // Dọn sau khi hộp mờ xong, tránh nội dung nháy trống lúc đóng.
    setTimeout(() => {
      setTep(null);
      setLoiChonTep(null);
      nop.reset();
    }, 200);
  };

  const chonTep = (danhSach: FileList | null) => {
    const f = danhSach?.[0];
    if (!f) return;
    nop.reset();
    if (!dungDinhDangGoi(f)) {
      setTep(null);
      setLoiChonTep(t('quanLyModule.upload.saiDinhDang'));
      return;
    }
    if (goiQuaLon(f)) {
      setTep(null);
      setLoiChonTep(t('quanLyModule.upload.quaLon', { toiDa: dungLuongVN(GOI_TOI_DA_BYTE) }));
      return;
    }
    setLoiChonTep(null);
    setTep(f);
  };

  return (
    <Dialog
      open={open}
      onClose={dong}
      title={t('quanLyModule.upload.tieuDe')}
      width={520}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={dong}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={nop.isPending}
            disabled={!tep || nop.isPending || nop.isSuccess}
            onClick={() => tep && nop.mutate(tep)}
          >
            {t('quanLyModule.upload.nopGoi')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 pt-2">
        <p className="text-sm leading-relaxed text-content-secondary">
          {t(kenh === 'tenant' ? 'quanLyModule.upload.moTenant' : 'quanLyModule.upload.mo')}
        </p>

        <div role="alert" className="flex gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
          <TriangleAlert size={16} aria-hidden className="mt-0.5 shrink-0 text-warning-600" />
          <p className="min-w-0 text-xs leading-relaxed text-warning-700">
            {t(kenh === 'tenant' ? 'quanLyModule.upload.canhBaoTenant' : 'quanLyModule.upload.canhBaoBaoMat')}
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => chonTep(e.target.files)}
        />

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={nop.isPending}
          className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-default bg-subtle px-4 py-6 text-center transition-colors hover:border-primary-400 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FileArchive size={28} className="text-content-muted" />
          {tep ? (
            <span className="text-sm text-content-primary">
              {tep.name} <span className="text-content-muted">({dungLuongVN(tep.size)})</span>
            </span>
          ) : (
            <span className="text-sm text-content-secondary">{t('quanLyModule.upload.chonTep')}</span>
          )}
          <span className="text-2xs text-content-muted">
            {t('quanLyModule.upload.dieuKien', { toiDa: dungLuongVN(GOI_TOI_DA_BYTE) })}
          </span>
        </button>

        {loiChonTep && <LoiTrongHop>{loiChonTep}</LoiTrongHop>}

        {nop.isError && <LoiNopGoi loi={nop.error} />}

        {nop.isSuccess && (
          <>
            <KetQuaNopGoi ketQua={nop.data} />
            <p className="text-xs leading-relaxed text-content-muted">
              {t(
                nop.data.status === 'PUBLISHED'
                  ? 'quanLyModule.upload.daDuyetNgay'
                  : kenh === 'tenant'
                    ? 'quanLyModule.upload.daVaoHangDoiTenant'
                    : 'quanLyModule.upload.daVaoHangDoi',
              )}
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
